import { cloneDeep } from 'lodash-es';
import { isAstEqual } from './ast/utils/equal.utils';

enum M66Type {
  '普通表盘' = '1',
  '动态表盘' = '2',
  '组件表盘' = '3',
  '相册表盘' = '4',
  '样式表盘' = '5',
  '游戏表盘' = '6'
}

const _findRef = (resoucesAst: Ast, ref: string) => {
  return resoucesAst.children.find((res) => '@' + res.attrsMap.name === ref);
};

const _getSlotWidgetAsts = (resoucesAst: Ast, item: Ast) => {
  const res = item.children.map((item) => {
    const widget = _findRef(resoucesAst, item.attrsMap.ref);
    if (widget) widget.children = _getSlotWidgetAsts(resoucesAst, widget);
    return widget;
  }) as Ast[];

  return res;
};

// 判断是否是M66样式表盘
// 样式表盘：
// 1. 一个样式下仅能有一个容器；
// 2. 容器下可以有多个组，但是不同组之间的元素都需要相同（除了引用的图片）；
// 3. 容器下每个组的元素引用都必须相同；
// 4. 容器下每个组的元素引用的元素都必须相同；
const isM66StyleType = (data: { slotLayouts: Ast[]; resoucesAst: Ast }) => {
  const { slotLayouts, resoucesAst } = data;

  // 1. 一个样式下仅能有一个容器；
  // 2. 容器下可以有多个组，但是不同组之间的元素都需要相同（除了引用的图片）；
  if (slotLayouts.length != 1) return false;

  const slotAst = resoucesAst.children.find(
    (res) =>
      res.tag === 'Slot' &&
      '@' + res.attrsMap.name === slotLayouts[0]?.attrsMap.ref
  );
  if (!slotAst) return false;

  // 判断容器下，每个widget下是否都相同（除了图片资源）
  const widgets = _getSlotWidgetAsts(resoucesAst, slotAst);
  // 判断widgets必须相同（处理图片资源）
  const widget0 = widgets[0];
  if (!widget0) return false;

  return widgets.every((widget) => {
    // 判断widgets必须相同（处理图片资源）
    return isAstEqual(
      widget,
      widget0,
      [
        'ref',
        'widgetName',
        'src',
        'editBox',
        'preview',
        '_shapeId',
        '_id',
        'unitIcon',
        'bg',
        'indicatorImage'
      ],
      // 是否忽略子元素顺序
      true
    );
  });
};

// 请帮我添加一个判断是否组件表盘的函数
const isM66ComponentType = (data: { slotLayouts: Ast[]; resoucesAst: Ast }) => {
  const { slotLayouts, resoucesAst } = data;

  const slotLayoutRefs = slotLayouts.map((item) => item.attrsMap.ref);
  const slotsAst = resoucesAst.children.filter(
    (res) =>
      res.tag === 'Slot' && slotLayoutRefs.includes('@' + res.attrsMap.name)
  );
  if (!slotsAst.length) return false;

  // 每个widget必须绑定跳转
  // m66的跳转使用widgetName，所以只需要判断widgetName是否存在即可
  // 且且widgetName使用数字表示，所以只需要判断是否是数字即可
  for (const slotAst of slotsAst) {
    const widgets = _getSlotWidgetAsts(resoucesAst, slotAst);
    // 只要有一个widget没有widgetName，就不是组件表盘，直接返回
    if (
      widgets.some(
        (widget) => /[0-9]{1,}/g.test(widget.attrsMap.widgetName ?? '') != true
      )
    )
      return false;
  }
  return true;
};

export const generateM66WatchfaceId = (watchfaceAst: Ast) => {
  watchfaceAst = cloneDeep(watchfaceAst);
  // 样式表盘5，组件表盘3，普通表盘1
  // 类型判断顺序 4 5 3 1
  // 2  6本身不支持，这里无需继续判断
  const _decideType = () => {
    const resoucesAst = watchfaceAst.children.find(
      (item) => item.tag === 'Resources'
    );
    const normalThemeAst = watchfaceAst.children.find(
      (item) => item.tag === 'Theme' && item.attrsMap.type === 'normal'
    );
    if (!resoucesAst || !normalThemeAst) return M66Type.普通表盘;

    // 判断是否是相册表盘
    const isAlbumAst =
      normalThemeAst.attrsMap['isPhotoAlbumWatchface'] == 'true';
    if (isAlbumAst) return M66Type.相册表盘;

    // 判断是否是样式表盘或组件表盘
    const slotLayouts = normalThemeAst.children.filter((item) => {
      const ref = item.attrsMap.ref;
      return resoucesAst.children.some((res) => {
        return res.tag === 'Slot' && '@' + res.attrsMap.name === ref;
      });
    });
    // 没有使用slot，普通表盘
    if (!slotLayouts.length) return M66Type.普通表盘;

    // 在使用slot的情况下，判断是否使用了表盘组件

    // 1. 样式表盘：
    //   1. 一个样式下仅能有一个容器；
    //   2. 容器下可以有多个组，但是不同组之间的元素都需要相同（除了引用的图片）；
    // 2. 组件表盘
    // 非样式表盘下，是组件表盘

    return isM66StyleType({ slotLayouts: slotLayouts, resoucesAst })
      ? M66Type.样式表盘
      : // 由于组件表盘需要添加跳转，但是m66禁用跳转，所以理论上不会出现组件表盘
      isM66ComponentType({ slotLayouts: slotLayouts, resoucesAst })
      ? M66Type.组件表盘
      : M66Type.普通表盘;
  };

  // 前4位是固定的，第5位是表盘类型
  const type = `2662${_decideType() ?? M66Type.普通表盘}`;
  return type + '0000';
};
